import './App.css';
import React from 'react';
import { createTourPackage, downloadTourPackage } from './export/tourPackage';
import PanoramaViewer from './editor/PanoramaViewer';
import {
  createAssetId,
  createDraftSnapshot,
  deleteAssets,
  getDraftVersion,
  getLatestDraftVersion,
  listDraftVersions,
  revokeObjectUrl,
  revokeProjectObjectUrls,
  restoreDraftSnapshot,
  saveAsset,
  saveDraftVersion,
} from './editor/draftStorage';

function uid(prefix = 'id') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createToneDataUrl(freq = 660, duration = 0.45) {
  const sampleRate = 22050;
  const samples = Math.floor(sampleRate * duration);
  const data = new Float32Array(samples);
  for (let i = 0; i < samples; i += 1) {
    const t = i / sampleRate;
    const envelope = Math.min(1, t * 12) * Math.max(0, 1 - t / duration);
    data[i] = Math.sin(2 * Math.PI * freq * t) * envelope * 0.35;
  }
  const buffer = new ArrayBuffer(44 + samples * 2);
  const view = new DataView(buffer);
  const writeStr = (offset, str) => {
    for (let i = 0; i < str.length; i += 1) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + samples * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, samples * 2, true);
  let offset = 44;
  for (let i = 0; i < samples; i += 1) {
    const s = Math.max(-1, Math.min(1, data[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return `data:audio/wav;base64,${btoa(binary)}`;
}

function createDemoPanoDataUrl(label, color) {
  const canvas = document.createElement('canvas');
  canvas.width = 2048;
  canvas.height = 1024;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createLinearGradient(0, 0, 2048, 1024);
  grad.addColorStop(0, color);
  grad.addColorStop(1, '#111827');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 2048, 1024);
  for (let i = 0; i < 36; i += 1) {
    ctx.fillStyle = `rgba(255,255,255,${0.04 + (i % 3) * 0.02})`;
    ctx.fillRect(i * 56, 0, 28, 1024);
  }
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.fillRect(640, 420, 768, 160);
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 72px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(label, 1024, 520);
  return canvas.toDataURL('image/jpeg', 0.85);
}

const SUPPORTED_PANO_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
  'image/bmp',
]);
const SUPPORTED_PANO_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif', 'bmp']);
const SAFE_PANO_MAX_WIDTH = 4096;
const SAFE_PANO_MAX_HEIGHT = 2048;
const MAX_PANO_COUNT = 30;
const MAX_SOURCE_FILE_BYTES = 64 * 1024 * 1024;
const MAX_UPLOAD_BATCH_BYTES = 320 * 1024 * 1024;
const MAX_SOURCE_PIXELS = 64 * 1024 * 1024;
const MAX_PROJECT_PANO_BYTES = 384 * 1024 * 1024;

let cachedTextureLimit;

function getFileExtension(fileName = '') {
  const match = fileName.toLowerCase().match(/\.([a-z0-9]+)$/);
  return match?.[1] || '';
}

function isSupportedPanoFile(file) {
  return SUPPORTED_PANO_TYPES.has(file.type) || SUPPORTED_PANO_EXTENSIONS.has(getFileExtension(file.name));
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('浏览器无法解码该图片'));
    };
    image.src = url;
  });
}

function releaseImage(image) {
  image.onload = null;
  image.onerror = null;
  image.src = '';
}

function yieldToBrowser() {
  return new Promise((resolve) => window.setTimeout(resolve, 0));
}

async function waitForCondition(predicate, timeout = 6000) {
  const deadline = Date.now() + timeout;
  while (Date.now() < deadline) {
    if (predicate()) return;
    await new Promise((resolve) => window.setTimeout(resolve, 100));
  }
  throw new Error('等待全景画面超时');
}

function canvasToJpegFile(canvas, originalFile) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('浏览器无法生成兼容的 JPG 图片'));
        return;
      }
      const baseName = originalFile.name.replace(/\.[^.]+$/, '') || 'panorama';
      resolve(new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.9);
  });
}

async function preparePanoFile(file, textureLimit) {
  if (!isSupportedPanoFile(file)) {
    throw new Error('仅支持 JPG/JPEG、PNG、WebP、GIF、AVIF、BMP 图片');
  }
  if (file.size > MAX_SOURCE_FILE_BYTES) {
    throw new Error('单张图片超过 64MB，请先压缩后再上传');
  }

  const image = await loadImage(file);
  let canvas;
  try {
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (!width || !height) throw new Error('无法读取图片尺寸');
    if (width * height > MAX_SOURCE_PIXELS) {
      throw new Error('图片像素过高，请先压缩到 6400 万像素以内');
    }

    const maxWidth = Math.min(textureLimit || SAFE_PANO_MAX_WIDTH, SAFE_PANO_MAX_WIDTH);
    const maxHeight = Math.min(textureLimit || SAFE_PANO_MAX_HEIGHT, SAFE_PANO_MAX_HEIGHT);
    const scale = Math.min(1, maxWidth / width, maxHeight / height);

    if (scale >= 1) {
      return { file, width, height, resized: false };
    }

    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));
    canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('浏览器无法处理该图片');
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    return {
      file: await canvasToJpegFile(canvas, file),
      width: targetWidth,
      height: targetHeight,
      resized: true,
    };
  } finally {
    if (canvas) {
      canvas.width = 1;
      canvas.height = 1;
    }
    releaseImage(image);
  }
}

function getWebGLTextureLimit() {
  if (cachedTextureLimit !== undefined) return cachedTextureLimit;
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl2') || canvas.getContext('webgl');
    const limit = context?.getParameter(context.MAX_TEXTURE_SIZE);
    context?.getExtension('WEBGL_lose_context')?.loseContext();
    cachedTextureLimit = Number.isFinite(limit) && limit > 0 ? limit : null;
  } catch {
    cachedTextureLimit = null;
  }
  return cachedTextureLimit;
}

function isBlobUrl(value) {
  return typeof value === 'string' && value.startsWith('blob:');
}

function projectPanoBytes(panos) {
  return (panos || []).reduce((total, pano) => total + (pano.sourceFile?.size || 0), 0);
}

function formatBytes(value) {
  if (!Number.isFinite(value) || value <= 0) return '0 MB';
  return `${Math.ceil(value / (1024 * 1024))} MB`;
}

function hasUnstoredProjectMedia(project) {
  if ((project.panos || []).some((pano) => isBlobUrl(pano.url) && !pano.assetId)) return true;
  if (
    (project.panos || []).some((pano) => isBlobUrl(pano.entryAudioUrl) && !pano.entryAudioAssetId)
  ) {
    return true;
  }
  return Object.values(project.stationsByPano || {}).some((stations) =>
    (stations || []).some((station) => isBlobUrl(station.audioUrl) && !station.audioAssetId)
  );
}

function normalizeProject(project) {
  const panos = Array.isArray(project?.panos) && project.panos.length ? project.panos : initialState().panos;
  const currentId = panos.some((pano) => pano.id === project?.currentId) ? project.currentId : panos[0].id;
  const stationsByPano = { ...(project?.stationsByPano || {}) };
  panos.forEach((pano) => {
    if (!stationsByPano[pano.id]) stationsByPano[pano.id] = [];
  });
  return { panos, currentId, stationsByPano };
}

const initialState = () => ({
  panos: [
    {
      id: 'demo',
      name: '示例全景',
      url: createDemoPanoDataUrl('示例全景', '#1d4ed8'),
      entryAudioUrl: null,
    },
  ],
  currentId: 'demo',
  stationsByPano: { demo: [] },
});

function App() {
  const initialProjectRef = React.useRef(null);
  if (!initialProjectRef.current) initialProjectRef.current = initialState();

  const panoInputRef = React.useRef(null);
  const audioInputRef = React.useRef(null);
  const entryAudioInputRef = React.useRef(null);
  const audioPlayerRef = React.useRef(null);

  const placeModeRef = React.useRef(false);
  const pendingLinkRef = React.useRef(null);
  const pendingAudioRef = React.useRef(null);
  const panosRef = React.useRef(initialProjectRef.current.panos);
  const currentIdRef = React.useRef(initialProjectRef.current.currentId);
  const stationsByPanoRef = React.useRef(initialProjectRef.current.stationsByPano);
  const navigateRef = React.useRef(null);
  const saveQueueRef = React.useRef(Promise.resolve());
  const uploadOperationRef = React.useRef(0);
  const projectEpochRef = React.useRef(0);
  const viewerRecoveryRef = React.useRef(false);

  const [panos, setPanos] = React.useState(initialProjectRef.current.panos);
  const [currentId, setCurrentId] = React.useState(initialProjectRef.current.currentId);
  const [stationsByPano, setStationsByPano] = React.useState(initialProjectRef.current.stationsByPano);
  const [viewerRecoveryKey, setViewerRecoveryKey] = React.useState(0);
  const [viewerContextLost, setViewerContextLost] = React.useState(false);
  const [panelOpen, setPanelOpen] = React.useState(true);
  const [placeMode, setPlaceMode] = React.useState(false);
  const [pendingLink, setPendingLink] = React.useState(null);
  const [targetPanoId, setTargetPanoId] = React.useState('');
  const [stationName, setStationName] = React.useState('');
  const [pendingAudio, setPendingAudio] = React.useState(null);
  const [tip, setTip] = React.useState('上传多张全景，添加导航站点：点击站点跳转并播报');
  const [playing, setPlaying] = React.useState(false);
  const [testLog, setTestLog] = React.useState([]);
  const [testing, setTesting] = React.useState(false);
  const [isDemoMode, setIsDemoMode] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [uploadingPanos, setUploadingPanos] = React.useState(false);
  const [versionHistory, setVersionHistory] = React.useState([]);
  const [showAllVersions, setShowAllVersions] = React.useState(false);
  const [restoringVersion, setRestoringVersion] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState({ kind: 'checking', label: '' });
  const [draftReady, setDraftReady] = React.useState(false);

  const currentPano = panos.find((p) => p.id === currentId) || panos[0];
  const currentStations = React.useMemo(
    () => stationsByPano[currentId] || [],
    [stationsByPano, currentId]
  );
  const displayedVersionHistory = showAllVersions ? versionHistory : versionHistory.slice(0, 5);

  React.useEffect(() => {
    placeModeRef.current = placeMode;
  }, [placeMode]);
  React.useEffect(() => {
    pendingLinkRef.current = pendingLink;
  }, [pendingLink]);
  React.useEffect(() => {
    pendingAudioRef.current = pendingAudio;
  }, [pendingAudio]);

  const replaceProjectState = React.useCallback((nextProject, options = {}) => {
    const normalized = normalizeProject(nextProject);
    if (options.revokePrevious) {
      revokeProjectObjectUrls(panosRef.current, stationsByPanoRef.current);
    }
    panosRef.current = normalized.panos;
    currentIdRef.current = normalized.currentId;
    stationsByPanoRef.current = normalized.stationsByPano;
    setPanos(normalized.panos);
    setCurrentId(normalized.currentId);
    setStationsByPano(normalized.stationsByPano);
    setTargetPanoId(normalized.panos.find((pano) => pano.id !== normalized.currentId)?.id || '');
    setViewerContextLost(false);
    return normalized;
  }, []);

  const queueVersion = React.useCallback((label, project = null) => {
    const currentProject = project || {
      panos: panosRef.current,
      currentId: currentIdRef.current,
      stationsByPano: stationsByPanoRef.current,
    };
    if (hasUnstoredProjectMedia(currentProject)) {
      const error = new Error('浏览器本机存储不可用，当前修改无法自动恢复');
      setSaveStatus({ kind: 'error', label: error.message });
      return Promise.resolve(null);
    }

    const snapshot = createDraftSnapshot(currentProject);
    setSaveStatus({ kind: 'saving', label });

    const task = async () => {
      const version = await saveDraftVersion({ label, snapshot });
      const history = await listDraftVersions();
      setVersionHistory(history);
      setSaveStatus({ kind: 'saved', label, updatedAt: version.createdAt });
      return version;
    };

    const queued = saveQueueRef.current.catch(() => undefined).then(task);
    saveQueueRef.current = queued;
    queued.catch((error) => {
      setSaveStatus({ kind: 'error', label: error.message || '自动保存失败' });
    });
    return queued;
  }, []);

  const persistAsset = React.useCallback(async (file, kind) => {
    const assetId = createAssetId(kind);
    try {
      await saveAsset(assetId, file);
      return assetId;
    } catch (error) {
      setSaveStatus({ kind: 'error', label: error.message || '素材保存失败' });
      throw error;
    }
  }, []);

  React.useEffect(() => {
    let active = true;

    const restoreLatestDraft = async () => {
      try {
        const history = await listDraftVersions();
        if (!active) return;
        setVersionHistory(history);
        if (!history.length) {
          setSaveStatus({ kind: 'idle', label: '尚未创建本机草稿' });
          return;
        }

        let restored = null;
        let restoredVersion = null;
        for (const version of history) {
          try {
            restored = await restoreDraftSnapshot(version.snapshot);
            restoredVersion = version;
            break;
          } catch (error) {
            console.warn(`无法恢复本机版本：${version.label}`, error);
          }
        }

        if (!restored || !restoredVersion) {
          throw new Error('没有可恢复的本机版本');
        }
        if (!active) {
          revokeProjectObjectUrls(restored.panos, restored.stationsByPano);
          return;
        }
        replaceProjectState(restored, { revokePrevious: true });
        setIsDemoMode(false);
        setTip(`已恢复本机草稿：${restoredVersion.label}`);
        setSaveStatus({
          kind: 'saved',
          label: `已恢复：${restoredVersion.label}`,
          updatedAt: restoredVersion.createdAt,
        });
      } catch (error) {
        if (!active) return;
        setSaveStatus({ kind: 'error', label: error.message || '无法恢复本机草稿' });
      } finally {
        if (active) setDraftReady(true);
      }
    };

    restoreLatestDraft();
    return () => {
      active = false;
      revokeProjectObjectUrls(panosRef.current, stationsByPanoRef.current);
    };
  }, [replaceProjectState]);

  React.useEffect(() => {
    const handleUnhandledRejection = (event) => {
      console.error('VR editor unhandled rejection', event.reason);
      queueVersion('异常前自动保存');
      setTip('检测到异常，正在保存最近草稿；刷新后可从本机版本恢复');
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, [queueVersion]);

  React.useEffect(() => {
    window.__VR_SAVE_DRAFT__ = () => queueVersion('渲染异常前自动保存');
    return () => {
      delete window.__VR_SAVE_DRAFT__;
    };
  }, [queueVersion]);

  const stopAudio = React.useCallback(() => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current.currentTime = 0;
      audioPlayerRef.current = null;
    }
    setPlaying(false);
  }, []);

  const playAudio = React.useCallback(
    (url, label) =>
      new Promise((resolve) => {
        stopAudio();
        if (!url) {
          resolve(false);
          return;
        }
        const audio = new Audio(url);
        audioPlayerRef.current = audio;
        setPlaying(true);
        setTip(`正在播报：${label || '语音'}`);
        const done = () => {
          setPlaying(false);
          resolve(true);
        };
        audio.addEventListener('ended', done, { once: true });
        audio.addEventListener('error', done, { once: true });
        audio.play().catch(() => {
          setTip('音频播放失败（浏览器可能拦截自动播放）');
          done();
        });
      }),
    [stopAudio]
  );

  const cancelPendingPlacement = React.useCallback(() => {
    const pendingAudio = pendingAudioRef.current;
    const pendingLink = pendingLinkRef.current;
    const urls = new Set([pendingAudio?.url, pendingLink?.audioUrl]);
    urls.forEach((url) => revokeObjectUrl(url));
    deleteAssets([pendingAudio?.assetId, pendingLink?.audioAssetId]).catch(() => undefined);
    pendingAudioRef.current = null;
    pendingLinkRef.current = null;
    setPendingAudio(null);
    setPendingLink(null);
    setPlaceMode(false);
    placeModeRef.current = false;
  }, []);

  const handleViewerError = React.useCallback((error) => {
    const reason = error?.message ? `：${error.message}` : '';
    setTip(`全景加载失败${reason}。请使用浏览器可打开的 JPG/JPEG 图片`);
  }, []);

  const handleViewerContextLost = React.useCallback(() => {
    if (viewerRecoveryRef.current) return;
    viewerRecoveryRef.current = true;
    setViewerContextLost(true);
    setTip('显卡资源已重置，正在恢复当前全景；本机草稿已自动保存');
    queueVersion('显卡恢复前自动保存');
    window.setTimeout(() => {
      setViewerRecoveryKey((key) => key + 1);
      setViewerContextLost(false);
      viewerRecoveryRef.current = false;
    }, 250);
  }, [queueVersion]);

  const goToPano = React.useCallback(
    async (targetId, options = {}) => {
      const { playEntry = true, fromStation = null } = options;
      const target = panosRef.current.find((pano) => pano.id === targetId);
      if (!target) {
        setTip('目标全景不存在');
        return false;
      }

      if (fromStation?.audioUrl) await playAudio(fromStation.audioUrl, fromStation.name);

      currentIdRef.current = targetId;
      setCurrentId(targetId);
      setPlaceMode(false);
      placeModeRef.current = false;
      setPendingLink(null);
      pendingLinkRef.current = null;
      setTip(`已进入：${target.name}`);

      if (playEntry && target.entryAudioUrl) {
        window.setTimeout(() => {
          playAudio(target.entryAudioUrl, `${target.name} 入场语音`);
        }, 400);
      }
      return true;
    },
    [playAudio]
  );

  React.useEffect(() => {
    navigateRef.current = goToPano;
  }, [goToPano]);

  const onUploadPano = async (event) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    if (!files.length) return;

    const operation = ++uploadOperationRef.current;
    const sourceBytes = files.reduce((total, file) => total + file.size, 0);
    const remainingSlots = MAX_PANO_COUNT - panosRef.current.length;
    if (files.length > remainingSlots) {
      setTip(`本项目最多保留 ${MAX_PANO_COUNT} 张全景；本次还可添加 ${Math.max(0, remainingSlots)} 张`);
      input.value = '';
      return;
    }
    if (sourceBytes > MAX_UPLOAD_BATCH_BYTES) {
      setTip(`本次素材共 ${formatBytes(sourceBytes)}，超过单次 ${formatBytes(MAX_UPLOAD_BATCH_BYTES)} 的安全预算`);
      input.value = '';
      return;
    }

    setUploadingPanos(true);
    setTip(`正在处理 ${files.length} 张全景图...`);
    const stagedPanos = [];
    let uploadCommitted = false;
    try {
      const preparedFiles = [];
      const rejected = [];
      const textureLimit = getWebGLTextureLimit();

      for (const file of files) {
        if (operation !== uploadOperationRef.current) return;
        try {
          preparedFiles.push(await preparePanoFile(file, textureLimit));
        } catch (error) {
          rejected.push(`${file.name}（${error.message || '浏览器无法解码，请转换为 JPG 后重试'}）`);
        }
        await yieldToBrowser();
      }

      if (operation !== uploadOperationRef.current) return;
      if (!preparedFiles.length) {
        setTip(`未上传成功：${rejected.join('；') || '未找到可用图片'}`);
        return;
      }

      const preparedBytes = preparedFiles.reduce((total, prepared) => total + prepared.file.size, 0);
      if (projectPanoBytes(panosRef.current) + preparedBytes > MAX_PROJECT_PANO_BYTES) {
        setTip(
          `处理后的全景将达到 ${formatBytes(projectPanoBytes(panosRef.current) + preparedBytes)}，超过项目 ${formatBytes(MAX_PROJECT_PANO_BYTES)} 的安全预算`
        );
        return;
      }

      const next = stagedPanos;
      for (let index = 0; index < preparedFiles.length; index += 1) {
        if (operation !== uploadOperationRef.current) {
          next.forEach((pano) => revokeObjectUrl(pano.url));
          await deleteAssets(next.map((pano) => pano.assetId)).catch(() => undefined);
          return;
        }
        const prepared = preparedFiles[index];
        const file = prepared.file;
        const assetId = await persistAsset(file, 'pano');
        next.push({
          id: uid(`pano-${index}`),
          name: file.name.replace(/\.[^.]+$/, ''),
          url: URL.createObjectURL(file),
          assetId,
          sourceFile: file,
          sourceName: file.name,
          width: prepared.width,
          height: prepared.height,
          entryAudioUrl: null,
          entryAudioName: null,
          wasResized: prepared.resized,
        });
      }

      if (operation !== uploadOperationRef.current) {
        next.forEach((pano) => revokeObjectUrl(pano.url));
        await deleteAssets(next.map((pano) => pano.assetId)).catch(() => undefined);
        return;
      }

      const existingPanos = panosRef.current;
      if (existingPanos.length + next.length > MAX_PANO_COUNT) {
        next.forEach((pano) => revokeObjectUrl(pano.url));
        await deleteAssets(next.map((pano) => pano.assetId)).catch(() => undefined);
        setTip(`上传期间项目已变更，最多只能保留 ${MAX_PANO_COUNT} 张全景`);
        return;
      }
      if (projectPanoBytes(existingPanos) + projectPanoBytes(next) > MAX_PROJECT_PANO_BYTES) {
        next.forEach((pano) => revokeObjectUrl(pano.url));
        await deleteAssets(next.map((pano) => pano.assetId)).catch(() => undefined);
        setTip(`上传期间项目素材已变更，超过 ${formatBytes(MAX_PROJECT_PANO_BYTES)} 的安全预算`);
        return;
      }
      const nextPanos = [...existingPanos, ...next];
      const nextStations = { ...stationsByPanoRef.current };
      next.forEach((pano) => {
        nextStations[pano.id] = [];
      });
      const project = {
        panos: nextPanos,
        currentId: next[0].id,
        stationsByPano: nextStations,
      };
      replaceProjectState(project);
      uploadCommitted = true;
      setIsDemoMode(false);
      const rejectedTip = rejected.length ? `；未添加 ${rejected.length} 张` : '';
      const resizedCount = next.filter((pano) => pano.wasResized).length;
      const resizedTip = resizedCount ? `；${resizedCount} 张已压缩到 4096×2048 以内` : '';
      setTip(`已上传 ${next.length} 张全景${resizedTip}${rejectedTip}`);
      await queueVersion(`上传 ${next.length} 张全景`, project);
    } catch (error) {
      if (!uploadCommitted) {
        stagedPanos.forEach((pano) => revokeObjectUrl(pano.url));
        await deleteAssets(stagedPanos.map((pano) => pano.assetId)).catch(() => undefined);
      }
      setTip(`上传失败：${error.message || '浏览器无法处理本批图片'}`);
    } finally {
      if (operation === uploadOperationRef.current) setUploadingPanos(false);
      input.value = '';
    }
  };

  const onPickStationAudio = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    const epoch = projectEpochRef.current;
    let assetId;
    try {
      assetId = await persistAsset(file, 'station-audio');
    } catch (error) {
      setTip(`无法保存站点语音：${error.message || '本机存储空间不足'}`);
      return;
    }
    if (epoch !== projectEpochRef.current) {
      await deleteAssets([assetId]).catch(() => undefined);
      return;
    }
    const previous = pendingAudioRef.current;
    revokeObjectUrl(previous?.url);
    deleteAssets([previous?.assetId]).catch(() => undefined);
    const audio = {
      name: file.name,
      url: URL.createObjectURL(file),
      assetId,
      sourceFile: file,
      sourceName: file.name,
    };
    pendingAudioRef.current = audio;
    setPendingAudio(audio);
    setTip(`已绑定站点语音「${file.name}」`);
  };

  const onPickEntryAudio = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';
    const panoId = currentIdRef.current;
    const pano = panosRef.current.find((item) => item.id === panoId);
    if (!file || !pano) return;

    const epoch = projectEpochRef.current;
    let assetId;
    try {
      assetId = await persistAsset(file, 'entry-audio');
    } catch (error) {
      setTip(`无法保存入场语音：${error.message || '本机存储空间不足'}`);
      return;
    }
    if (epoch !== projectEpochRef.current) {
      await deleteAssets([assetId]).catch(() => undefined);
      return;
    }
    const url = URL.createObjectURL(file);
    const nextPanos = panosRef.current.map((item) =>
      item.id === panoId
        ? {
            ...item,
            entryAudioUrl: url,
            entryAudioName: file.name,
            entryAudioAssetId: assetId,
            entryAudioFile: file,
            entryAudioSourceName: file.name,
          }
        : item
    );
    const project = {
      panos: nextPanos,
      currentId: currentIdRef.current,
      stationsByPano: stationsByPanoRef.current,
    };
    replaceProjectState(project);
    revokeObjectUrl(pano.entryAudioUrl);
    setTip(`已为「${pano.name}」设置入场语音`);
    await queueVersion(`设置「${pano.name}」入场语音`, project);
  };

  const startPlaceLink = () => {
    if (panosRef.current.length < 2) {
      setTip('请先至少上传 2 张全景图，才能做站点跳转');
      return;
    }
    const current = currentIdRef.current;
    const target =
      targetPanoId && targetPanoId !== current
        ? targetPanoId
        : panosRef.current.find((pano) => pano.id !== current)?.id;
    if (!target) {
      setTip('请选择一个不同的目标全景');
      return;
    }
    const targetPano = panosRef.current.find((pano) => pano.id === target);
    const audio = pendingAudioRef.current;
    const link = {
      targetPanoId: target,
      name: stationName.trim() || `前往 ${targetPano?.name || '下一站'}`,
      audioUrl: audio?.url || null,
      audioName: audio?.name || null,
      audioAssetId: audio?.assetId || null,
      audioFile: audio?.sourceFile || null,
      audioSourceName: audio?.sourceName || null,
    };
    pendingAudioRef.current = null;
    setPendingAudio(null);
    setPendingLink(link);
    pendingLinkRef.current = link;
    setPlaceMode(true);
    placeModeRef.current = true;
    setTip(`放置模式：在全景上点击，放置通往「${targetPano?.name}」的导航点`);
  };

  const onViewerClick = React.useCallback(
    ({ yaw, pitch }) => {
      if (!placeModeRef.current || !pendingLinkRef.current) return;
      if (yaw == null || pitch == null) {
        setTip('未获取到点击坐标，请再点一次画面');
        return;
      }
      const link = pendingLinkRef.current;
      const station = {
        id: uid('station'),
        name: link.name,
        yaw,
        pitch,
        targetPanoId: link.targetPanoId,
        audioUrl: link.audioUrl,
        audioName: link.audioName,
        audioAssetId: link.audioAssetId,
        audioFile: link.audioFile,
        audioSourceName: link.audioSourceName,
      };
      const panoId = currentIdRef.current;
      const nextStations = {
        ...stationsByPanoRef.current,
        [panoId]: [...(stationsByPanoRef.current[panoId] || []), station],
      };
      const project = {
        panos: panosRef.current,
        currentId: panoId,
        stationsByPano: nextStations,
      };
      replaceProjectState(project);
      setPlaceMode(false);
      placeModeRef.current = false;
      setPendingLink(null);
      pendingLinkRef.current = null;
      setStationName('');
      setTip(`已添加导航站「${station.name}」`);
      queueVersion(`添加导航站「${station.name}」`, project);
    },
    [queueVersion, replaceProjectState]
  );

  const onSelectStation = React.useCallback(
    (stationId) => {
      const station = (stationsByPanoRef.current[currentIdRef.current] || []).find(
        (item) => item.id === stationId
      );
      if (!station) return;
      if (station.targetPanoId) {
        navigateRef.current?.(station.targetPanoId, { playEntry: true, fromStation: station });
      } else if (station.audioUrl) {
        playAudio(station.audioUrl, station.name);
      }
    },
    [playAudio]
  );

  const removeStation = (id) => {
    const panoId = currentIdRef.current;
    const stations = stationsByPanoRef.current[panoId] || [];
    const removed = stations.find((station) => station.id === id);
    if (!removed) return;
    const nextStations = {
      ...stationsByPanoRef.current,
      [panoId]: stations.filter((station) => station.id !== id),
    };
    const project = {
      panos: panosRef.current,
      currentId: currentIdRef.current,
      stationsByPano: nextStations,
    };
    replaceProjectState(project);
    revokeObjectUrl(removed.audioUrl);
    setTip(`已删除导航站「${removed.name}」`);
    queueVersion(`删除导航站「${removed.name}」`, project);
  };

  const switchPanoManual = (id) => {
    goToPano(id, { playEntry: true, fromStation: null });
  };

  const removePano = (id) => {
    const currentPanos = panosRef.current;
    if (currentPanos.length <= 1) {
      setTip('至少保留一个全景');
      return;
    }
    uploadOperationRef.current += 1;
    projectEpochRef.current += 1;
    const removedPano = currentPanos.find((pano) => pano.id === id);
    const nextPanos = currentPanos.filter((pano) => pano.id !== id);
    const removedStations = [
      ...(stationsByPanoRef.current[id] || []),
      ...Object.entries(stationsByPanoRef.current)
        .filter(([panoId]) => panoId !== id)
        .flatMap(([, stations]) => (stations || []).filter((station) => station.targetPanoId === id)),
    ];
    const nextStations = {};
    Object.entries(stationsByPanoRef.current).forEach(([panoId, stations]) => {
      if (panoId !== id) {
        nextStations[panoId] = (stations || []).filter((station) => station.targetPanoId !== id);
      }
    });
    const project = {
      panos: nextPanos,
      currentId: currentIdRef.current === id ? nextPanos[0].id : currentIdRef.current,
      stationsByPano: nextStations,
    };
    replaceProjectState(project);
    revokeObjectUrl(removedPano?.url);
    revokeObjectUrl(removedPano?.entryAudioUrl);
    removedStations.forEach((station) => revokeObjectUrl(station.audioUrl));
    setTip(`已删除全景「${removedPano?.name || '场景'}」`);
    if (!isDemoMode) queueVersion(`删除全景「${removedPano?.name || '场景'}」`, project);
  };

  const resetToHome = React.useCallback(async () => {
    uploadOperationRef.current += 1;
    projectEpochRef.current += 1;
    stopAudio();
    cancelPendingPlacement();
    const home = initialState();
    replaceProjectState(home, { revokePrevious: true });
    setTargetPanoId('');
    setStationName('');
    setTestLog([]);
    setIsDemoMode(false);
    setTip('已重置为初始场景；此前版本仍可在本机版本中恢复');
    await queueVersion('重置为初始场景', home);
  }, [cancelPendingPlacement, queueVersion, replaceProjectState, stopAudio]);

  const exportTour = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { blob, fileName, manifest } = await createTourPackage({
        panos: panosRef.current,
        currentId: currentIdRef.current,
        stationsByPano: stationsByPanoRef.current,
        onProgress: setTip,
      });
      downloadTourPackage(blob, fileName);
      setTip(`已导出 ${manifest.panos.length} 个场景的部署包。上传并解压后即可分享链接`);
    } catch (error) {
      setTip(`导出失败：${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const loadDemoTour = async () => {
    uploadOperationRef.current += 1;
    projectEpochRef.current += 1;
    stopAudio();
    cancelPendingPlacement();
    if (!isDemoMode) await queueVersion('加载演示前自动备份');
    const tone1 = createToneDataUrl(520, 0.5);
    const tone2 = createToneDataUrl(660, 0.5);
    const tone3 = createToneDataUrl(780, 0.5);
    const p1 = {
      id: 'room-a',
      name: '前台大厅',
      url: createDemoPanoDataUrl('前台大厅', '#1d4ed8'),
      entryAudioUrl: tone1,
      entryAudioName: 'demo-a.wav',
    };
    const p2 = {
      id: 'room-b',
      name: '展厅',
      url: createDemoPanoDataUrl('展厅', '#047857'),
      entryAudioUrl: tone2,
      entryAudioName: 'demo-b.wav',
    };
    const p3 = {
      id: 'room-c',
      name: '会议室',
      url: createDemoPanoDataUrl('会议室', '#b45309'),
      entryAudioUrl: tone3,
      entryAudioName: 'demo-c.wav',
    };
    const stations = {
      'room-a': [
        {
          id: 'a-to-b',
          name: '去展厅',
          yaw: 0.8,
          pitch: 0,
          targetPanoId: 'room-b',
          audioUrl: tone2,
          audioName: 'to-b.wav',
        },
      ],
      'room-b': [
        {
          id: 'b-to-c',
          name: '去会议室',
          yaw: -0.6,
          pitch: 0.05,
          targetPanoId: 'room-c',
          audioUrl: tone3,
          audioName: 'to-c.wav',
        },
        {
          id: 'b-to-a',
          name: '回前台',
          yaw: 2.2,
          pitch: 0,
          targetPanoId: 'room-a',
          audioUrl: tone1,
          audioName: 'to-a.wav',
        },
      ],
      'room-c': [
        {
          id: 'c-to-b',
          name: '回展厅',
          yaw: 1.2,
          pitch: -0.05,
          targetPanoId: 'room-b',
          audioUrl: tone2,
          audioName: 'back-b.wav',
        },
      ],
    };
    replaceProjectState({ panos: [p1, p2, p3], currentId: 'room-a', stationsByPano: stations }, { revokePrevious: true });
    setIsDemoMode(true);
    setTip('演示中：点导航站可跳转。要退出请点「退出演示」');
  };

  const restoreVersion = async (versionId, options = {}) => {
    const { backupCurrent = true } = options;
    if (restoringVersion) return;
    setRestoringVersion(true);
    setTip('正在恢复本机版本...');
    try {
      const version = await getDraftVersion(versionId);
      if (!version) throw new Error('该本机版本已不存在');
      if (backupCurrent && !isDemoMode) await queueVersion('回退前自动备份');

      const restored = await restoreDraftSnapshot(version.snapshot);
      uploadOperationRef.current += 1;
      projectEpochRef.current += 1;
      stopAudio();
      cancelPendingPlacement();
      const project = replaceProjectState(restored, { revokePrevious: true });
      setStationName('');
      setIsDemoMode(false);
      setTip(`已恢复版本：${version.label}`);
      await queueVersion(`恢复版本：${version.label}`, project);
    } catch (error) {
      setTip(`恢复失败：${error.message || '本机版本不可用'}`);
    } finally {
      setRestoringVersion(false);
    }
  };

  const exitDemo = async () => {
    try {
      const latest = await getLatestDraftVersion();
      if (latest) {
        await restoreVersion(latest.id, { backupCurrent: false });
      } else {
        await resetToHome();
      }
    } catch {
      await resetToHome();
    }
  };

  const saveCurrentVersion = async () => {
    if (isDemoMode) {
      setTip('演示导览不会覆盖本机草稿');
      return;
    }
    await queueVersion('手动保存');
    setTip('已创建本机版本');
  };

  const pushLog = (msg, ok = true) => {
    setTestLog((prev) => [...prev, { msg, ok, t: Date.now() }]);
  };

  const runFullTest = async () => {
    if (testing) return;
    setTesting(true);
    setTestLog([]);
    const assert = (cond, msg) => {
      pushLog(msg, !!cond);
      if (!cond) throw new Error(msg);
    };

    try {
      pushLog('开始全量功能测试…', true);
      await loadDemoTour();
      await new Promise((r) => setTimeout(r, 600));

      assert(panosRef.current.length === 3, '✓ 已加载 3 个演示全景');
      assert(
        (stationsByPanoRef.current['room-a'] || []).length === 1,
        '✓ 前台大厅有 1 个导航站'
      );
      assert(currentIdRef.current === 'room-a', '✓ 当前位于前台大厅');

      await waitForCondition(() => document.querySelectorAll('.psv-marker').length >= 1);
      const markerCount = document.querySelectorAll('.psv-marker').length;
      assert(markerCount >= 1, `✓ 场景中有标记点（${markerCount}）`);

      const st = stationsByPanoRef.current['room-a'][0];
      await navigateRef.current(st.targetPanoId, { playEntry: true, fromStation: st });
      await new Promise((r) => setTimeout(r, 900));
      assert(currentIdRef.current === 'room-b', '✓ 点击站点后跳转到展厅');
      assert(
        (stationsByPanoRef.current['room-b'] || []).length === 2,
        '✓ 展厅导航站保留完好'
      );

      const st2 = stationsByPanoRef.current['room-b'].find((s) => s.targetPanoId === 'room-c');
      await navigateRef.current(st2.targetPanoId, { playEntry: true, fromStation: st2 });
      await new Promise((r) => setTimeout(r, 900));
      assert(currentIdRef.current === 'room-c', '✓ 再跳转到会议室');

      await navigateRef.current('room-a', { playEntry: false });
      await new Promise((r) => setTimeout(r, 500));
      assert(currentIdRef.current === 'room-a', '✓ 可手动返回前台');

      // 模拟上传 + 放置链路（程序化）
      const extra = {
        id: 'room-d',
        name: '办公区',
        url: createDemoPanoDataUrl('办公区', '#7c3aed'),
        entryAudioUrl: createToneDataUrl(900, 0.35),
      };
      setPanos((prev) => {
        const next = [...prev, extra];
        panosRef.current = next;
        return next;
      });
      setStationsByPano((prev) => {
        const next = {
          ...prev,
          'room-d': [],
          'room-a': [
            ...(prev['room-a'] || []),
            {
              id: 'a-to-d',
              name: '去办公区',
              yaw: -1.2,
              pitch: 0.1,
              targetPanoId: 'room-d',
              audioUrl: createToneDataUrl(440, 0.3),
              audioName: 'to-d.wav',
            },
          ],
        };
        stationsByPanoRef.current = next;
        return next;
      });
      await new Promise((r) => setTimeout(r, 200));
      await navigateRef.current('room-a', { playEntry: false });
      await new Promise((r) => setTimeout(r, 500));
      const toD = (stationsByPanoRef.current['room-a'] || []).find((s) => s.targetPanoId === 'room-d');
      assert(!!toD, '✓ 可新增导航站');
      await navigateRef.current(toD.targetPanoId, { playEntry: true, fromStation: toD });
      await new Promise((r) => setTimeout(r, 700));
      assert(currentIdRef.current === 'room-d', '✓ 新站点跳转成功');

      // 语音播放测试
      const played = await playAudio(createToneDataUrl(600, 0.25), '测试音');
      assert(played, '✓ 语音播报可播放');

      pushLog('全部测试通过', true);
      setTip('全量测试通过：上传/导航跳转/语音播报均正常');
      window.__VR_TEST_RESULT__ = { ok: true, logs: true };
    } catch (err) {
      pushLog(`失败：${err.message}`, false);
      setTip(`测试失败：${err.message}`);
      window.__VR_TEST_RESULT__ = { ok: false, error: err.message };
    } finally {
      setTesting(false);
    }
  };

  // 暴露给自动化脚本
  React.useEffect(() => {
    window.__VR_APP__ = {
      runFullTest,
      loadDemoTour,
      resetToHome,
      getState: () => ({
        currentId: currentIdRef.current,
        panos: panosRef.current.map((p) => ({ id: p.id, name: p.name })),
        stationsByPano: stationsByPanoRef.current,
        isDemoMode,
      }),
      goToPano: (id) => navigateRef.current?.(id, { playEntry: false }),
    };
  });

  const otherPanos = panos.filter((p) => p.id !== currentId);

  return (
    <div className="App" data-testid="vr-app">
      <div className={`toolbar ${panelOpen ? 'open' : ''}`}>
        <div className="toolbar-header">
          <strong>VR 导览操作台</strong>
          <button type="button" className="icon-btn" onClick={() => setPanelOpen((v) => !v)}>
            {panelOpen ? '收起' : '展开'}
          </button>
        </div>

        {panelOpen && (
          <>
            <p className="tip" data-testid="tip">
              {tip}
              {playing ? ' 🔊' : ''}
            </p>
            <div className={`save-status ${saveStatus.kind}`} data-testid="save-status">
              {saveStatus.kind === 'checking'
                ? '正在检查本机草稿...'
                : `本机草稿：${saveStatus.label || '等待保存'}`}
              {viewerContextLost ? '；正在恢复画面' : ''}
            </div>

            <div className="row">
              <button
                type="button"
                data-testid="upload-pano"
                disabled={uploadingPanos || !draftReady}
                onClick={() => panoInputRef.current?.click()}
              >
                {uploadingPanos ? '正在校验全景图…' : '上传全景图'}
              </button>
              <button
                type="button"
                className="ghost"
                data-testid="export-tour"
                disabled={exporting || !draftReady}
                onClick={exportTour}
              >
                {exporting ? '正在导出…' : '导出部署包'}
              </button>
              {isDemoMode ? (
                <button
                  type="button"
                  className="danger"
                  data-testid="exit-demo"
                  disabled={!draftReady || restoringVersion}
                  onClick={exitDemo}
                >
                  退出演示
                </button>
              ) : (
                <button
                  type="button"
                  className="ghost"
                  data-testid="load-demo"
                  disabled={!draftReady || restoringVersion}
                  onClick={loadDemoTour}
                >
                  加载演示导览
                </button>
              )}
              <input
                ref={panoInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.gif,.avif,.bmp,image/jpeg,image/png,image/webp,image/gif,image/avif,image/bmp"
                multiple
                hidden
                onChange={onUploadPano}
              />
            </div>

            {isDemoMode && (
              <div className="demo-banner">
                当前为演示模式（前台→展厅→会议室）
                <button type="button" className="danger" disabled={!draftReady || restoringVersion} onClick={exitDemo}>
                  退出演示，恢复草稿
                </button>
              </div>
            )}

            <div className="row">
              <button
                type="button"
                className="ghost"
                data-testid="run-test"
                disabled={testing || !draftReady || restoringVersion}
                onClick={runFullTest}
              >
                {testing ? '测试中…' : '全量自测'}
              </button>
              <button type="button" className="ghost" onClick={stopAudio}>
                停止语音
              </button>
              <button type="button" className="ghost" disabled={!draftReady || restoringVersion} onClick={saveCurrentVersion}>
                保存版本
              </button>
              <button
                type="button"
                className="ghost"
                data-testid="reset-home"
                disabled={!draftReady || restoringVersion}
                onClick={resetToHome}
              >
                重置
              </button>
            </div>

            <div className="section version-history" data-testid="version-history">
              <div className="section-title">本机版本（{versionHistory.length}）</div>
              {versionHistory.length === 0 ? (
                <div className="empty">修改导览后会自动记录在这里</div>
              ) : (
                <>
                  <div className="list">
                    {displayedVersionHistory.map((version) => (
                      <div key={version.id} className="version-item">
                        <div className="station-main">
                          <span>{version.label}</span>
                          <small>{new Date(version.createdAt).toLocaleString()}</small>
                        </div>
                        <button
                          type="button"
                          className="ghost mini"
                          data-testid={`restore-${version.id}`}
                          disabled={!draftReady || restoringVersion}
                          onClick={() => restoreVersion(version.id)}
                        >
                          恢复
                        </button>
                      </div>
                    ))}
                  </div>
                  {versionHistory.length > 5 && (
                    <div className="row tight">
                      <button
                        type="button"
                        className="ghost"
                        data-testid="toggle-version-history"
                        aria-expanded={showAllVersions}
                        onClick={() => setShowAllVersions((visible) => !visible)}
                      >
                        {showAllVersions ? '收起版本' : '查看更多版本'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="section">
              <div className="section-title">全景列表（{panos.length}）</div>
              <div className="list">
                {panos.map((p) => (
                  <div key={p.id} className={`pano-item ${p.id === currentId ? 'current' : ''}`}>
                    <button type="button" className="list-item" disabled={!draftReady} onClick={() => switchPanoManual(p.id)}>
                      {p.name}
                      {p.entryAudioUrl ? ' 🔊' : ''}
                    </button>
                    <button type="button" className="ghost mini" disabled={!draftReady || restoringVersion} onClick={() => removePano(p.id)}>
                      删
                    </button>
                  </div>
                ))}
              </div>
              <div className="row tight">
                <button
                  type="button"
                  className="ghost"
                  disabled={!draftReady || restoringVersion}
                  onClick={() => entryAudioInputRef.current?.click()}
                >
                  为当前场景设入场语音
                </button>
                <input
                  ref={entryAudioInputRef}
                  type="file"
                  accept="audio/*"
                  hidden
                  onChange={onPickEntryAudio}
                />
              </div>
              {currentPano?.entryAudioName && (
                <div className="meta">入场语音：{currentPano.entryAudioName}</div>
              )}
            </div>

            <div className="section">
              <div className="section-title">添加导航站点</div>
              <label className="field">
                站点名称
                <input
                  disabled={!draftReady || restoringVersion}
                  value={stationName}
                  onChange={(e) => setStationName(e.target.value)}
                  placeholder="例如：去展厅"
                />
              </label>
              <label className="field">
                跳转到
                <select
                  disabled={!draftReady || restoringVersion}
                  value={targetPanoId || otherPanos[0]?.id || ''}
                  onChange={(e) => setTargetPanoId(e.target.value)}
                  data-testid="target-select"
                >
                  {otherPanos.length === 0 && <option value="">请先上传更多全景</option>}
                  {otherPanos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="row">
                <button
                  type="button"
                  className="ghost"
                  disabled={!draftReady || restoringVersion}
                  onClick={() => audioInputRef.current?.click()}
                >
                  {pendingAudio ? `语音:${pendingAudio.name}` : '选择站点语音(可选)'}
                </button>
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/*"
                  hidden
                  onChange={onPickStationAudio}
                />
              </div>
              <div className="row">
                <button
                  type="button"
                  className={placeMode ? 'active' : ''}
                  data-testid="place-link"
                  disabled={!draftReady || restoringVersion}
                  onClick={startPlaceLink}
                >
                  {placeMode ? '放置中…请点全景' : '开始放置导航点'}
                </button>
                {placeMode && (
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => {
                      cancelPendingPlacement();
                      setTip('已取消放置');
                    }}
                  >
                    取消
                  </button>
                )}
              </div>
            </div>

            <div className="section">
              <div className="section-title">
                当前场景导航站（{currentStations.length}）
              </div>
              {currentStations.length === 0 && (
                <div className="empty">暂无导航站。选择目标全景后点击「开始放置导航点」</div>
              )}
              <div className="list">
                {currentStations.map((s) => {
                  const target = panos.find((p) => p.id === s.targetPanoId);
                  return (
                    <div key={s.id} className="station-item">
                      <div className="station-main">
                        <span>{s.name}</span>
                        <small>
                          → {target?.name || s.targetPanoId}
                          {s.audioName ? ` · ${s.audioName}` : ''}
                        </small>
                      </div>
                      <div className="station-actions">
                        <button
                          type="button"
                          data-testid={`goto-${s.id}`}
                          disabled={!draftReady}
                          onClick={() =>
                            goToPano(s.targetPanoId, { playEntry: true, fromStation: s })
                          }
                        >
                          前往
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          disabled={!draftReady || restoringVersion}
                          onClick={() => removeStation(s.id)}
                        >
                          删
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {testLog.length > 0 && (
              <div className="section test-log" data-testid="test-log">
                <div className="section-title">自测日志</div>
                {testLog.map((l, i) => (
                  <div key={`${l.t}-${i}`} className={l.ok ? 'ok' : 'fail'}>
                    {l.msg}
                  </div>
                ))}
              </div>
            )}

            <ol className="help">
              <li>上传至少 2 张公司 360° 全景图</li>
              <li>选目标场景 + 可选语音，点「开始放置导航点」</li>
              <li>在全景画面点击放置导航站</li>
              <li>参观时点击导航站：播报语音并跳转下一站</li>
              <li>也可点「加载演示导览」先体验完整流程</li>
            </ol>
          </>
        )}
      </div>

      {currentPano?.url && (
        <PanoramaViewer
          key={viewerRecoveryKey}
          src={currentPano.url}
          stations={currentStations}
          onPlace={onViewerClick}
          onSelectStation={onSelectStation}
          onError={handleViewerError}
          onContextLost={handleViewerContextLost}
        />
      )}
    </div>
  );
}

export default App;
